/* eslint-disable no-undef */
import { authApi as authApiUrl, guestApi as guestApiUrl } from '../../utils/urls';
import { rootApi } from '../../services/rootApi';

/**
 * RTK Query API slice for authentication
 */
export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: ({ identity, credential }) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'login', data: { identity, credential } },
      }),
      transformResponse: (response) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Use updateQueryData instead of upsertQueryData when we need to merge with existing data
          dispatch(authApi.util.updateQueryData('status', undefined, (prev) => {
            const nextUser = (data && 'user' in data) ? (data.user && Object.keys(data.user || {}).length > 0 ? data.user : null) : (prev?.user ?? null);

            return ({
              ...prev,
              user: nextUser,
              sessionId: (data && 'sessionId' in data) ? data.sessionId : (prev?.sessionId ?? null),
              roles: (data && 'roles' in data) ? data.roles : (prev?.roles ?? null),
              loggedIn: true,
            });
          }));
          // NOTE: We don't invalidate Settings/StoreCurrency here because:
          // 1. Tag invalidation triggers immediate refetch
          // 2. At this point, token might not be persisted to localStorage yet
          // 3. This causes Settings/StoreCurrency to be called without token → 401/400 errors
          // 4. Instead, let components refetch naturally when they mount on dashboard
          // 5. The token will be available by then via AuthContext.wrap → persistAuth
        } catch {}
      },
    }),
    logout: builder.mutation({
      query: () => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'logout' },
      }),
    }),
    status: builder.query({
      query: () => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'status' },
      }),
      transformResponse: (response) => response.data,
      providesTags: ['Auth'],
    }),
    incarnate: builder.mutation({
      query: ({ userId }) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'incarnate', data: { userId } },
      }),
      transformResponse: (response) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Use updateQueryData instead of upsertQueryData when we need to merge with existing data
          dispatch(authApi.util.updateQueryData('status', undefined, (prev) => {
            const nextUser = (data && 'user' in data) ? (data.user && Object.keys(data.user || {}).length > 0 ? data.user : null) : (prev?.user ?? null);

            return ({
              ...prev,
              user: nextUser,
              sessionId: (data && 'sessionId' in data) ? data.sessionId : (prev?.sessionId ?? null),
              roles: (data && 'roles' in data) ? data.roles : (prev?.roles ?? null),
              loggedIn: true,
            });
          }));
          dispatch(rootApi.util.invalidateTags(['Settings', 'StoreCurrency']));
        } catch {}
      },
    }),
    wakeUp: builder.mutation({
      query: () => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'wakeUp' },
      }),
      transformResponse: (response) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Use updateQueryData instead of upsertQueryData when we need to merge with existing data
          dispatch(authApi.util.updateQueryData('status', undefined, (prev) => {
            const nextUser = (data && 'user' in data) ? (data.user && Object.keys(data.user || {}).length > 0 ? data.user : null) : (prev?.user ?? null);

            return ({
              ...prev,
              user: nextUser,
              sessionId: (data && 'sessionId' in data) ? data.sessionId : (prev?.sessionId ?? null),
              roles: (data && 'roles' in data) ? data.roles : (prev?.roles ?? null),
              loggedIn: true,
            });
          }));
          dispatch(rootApi.util.invalidateTags(['Settings', 'StoreCurrency']));
        } catch {}
      },
    }),
    registrationAndLogin: builder.mutation({
      query: (regInfo) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'logister', data: regInfo },
      }),
      transformResponse: (response) => response.data,
    }),
    guestRegister: builder.mutation({
      query: (regInfo) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'guestRegister', data: regInfo },
      }),
      transformResponse: (response) => response.data,
    }),
    getGuestInfo: builder.query({
      query: ({ id }) => ({
        url: guestApiUrl(),
        method: 'POST',
        body: { method: 'getGuestInfo', data: { id } },
      }),
    }),
    hashLogin: builder.mutation({
      query: ({ c2hash }) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'loginCdk', data: { hash: c2hash } },
      }),
      transformResponse: (response) => response.data,
    }),
    loginGoogle: builder.mutation({
      query: (jwtToken) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'loginGoogle', data: { idToken: jwtToken } },
      }),
      transformResponse: (response) => response.data,
    }),
    requestNewPassword: builder.mutation({
      query: ({ email }) => ({
        url: authApiUrl(),
        method: 'POST',
        body: {
          method: 'requestNewPassword',
          data: {
            callbackUrl: 'https://app.coursio.com/restore-password/',
            email,
          },
        },
      }),
    }),
    restorePassword: builder.mutation({
      query: (data) => ({
        url: authApiUrl(),
        method: 'POST',
        body: { method: 'restorePassword', data },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useStatusQuery: useAuthStatusQuery,
  useLazyStatusQuery,
  useIncarnateMutation,
  useWakeUpMutation,
  useRegistrationAndLoginMutation,
  useGuestRegisterMutation,
  useGetGuestInfoQuery,
  useHashLoginMutation,
  useLoginGoogleMutation,
  useRequestNewPasswordMutation,
  useRestorePasswordMutation,
} = authApi;
